import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import { useHomeOpportunities } from './hooks/useHomeOpportunities';
import { useHomeForumPosts } from './hooks/useHomeForumPosts';
import { useHomePlugYourWork } from './hooks/useHomePlugYourWork';
import { useHomeBlogPosts } from './hooks/useHomeBlogPosts';
import { useLatestPodcastEpisode } from './hooks/useLatestPodcastEpisode';
import { useHomeAds } from './hooks/useHomeAds';
import { useAuthenticatedApi } from './api';
import { toast } from 'react-hot-toast';
import { getAuthorName } from './utils/authorName';
import { formatTypeLabels } from './utils/formatTypeLabel';
import { trackEvent } from './utils/analytics';
import './Home.css';

export default function Home() {
  const { isAuthenticated, isLoading, loginWithPopup } = useAuth0();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  // Shared modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'source-request' or 'steal-pitch'
  const [modalSubject, setModalSubject] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use separate React Query hooks for independent loading
  const { data: opportunities, isLoading: loadingOpportunities } = useHomeOpportunities();
  const { data: forumPosts, isLoading: loadingForum, isError: forumLoadError, refetch: refetchForumPosts } = useHomeForumPosts();
  const { data: plugYourWork, isLoading: loadingPlugYourWork, error: plugYourWorkError } = useHomePlugYourWork();
  const { data: blogPosts, isLoading: loadingBlog, error: blogError } = useHomeBlogPosts();
  const { data: latestEpisode, isLoading: loadingLatestEpisode } = useLatestPodcastEpisode();
  const { data: ads = [] } = useHomeAds({ type: 'course' });
  const { data: paidAds = [] } = useHomeAds({ type: 'paid' });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years === 1 ? '' : 's'} ago`;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Category color coding (matching Opportunities.js)
  const getCategoryColor = (category) => {
    const colorMap = {
      'Arts and Culture': '#3864B4',
      'Creative': '#b438a2',
      'Content': '#b48838',
      'News': '#38B44a'
    };
    return colorMap[category] || '#666';
  };

  // Thread type color coding
  const getThreadTypeColor = (type) => {
    const colorMap = {
      'source-request': '#e67e7e',      // Coral
      'steal-pitch': '#607d8b',         // Slate blue
      'discussion': '#38b44a',          // Green (matching News)
      'plug-your-work': '#e74c3c'       // Red
    };
    return colorMap[type] || '#e0e0e0';  // Default gray for unknown types
  };

  const getOpportunityCategories = (opportunity) => {
    if (!opportunity.areas) return [];
    
    const areas = opportunity.areas.split(',').map(a => a.trim());
    const categories = [];
    
    // Map areas directly to categories
    areas.forEach(area => {
      const normalized = area.toLowerCase();
      if (normalized.includes('arts') || normalized.includes('culture')) {
        if (!categories.includes('Arts and Culture')) categories.push('Arts and Culture');
      } else if (normalized === 'creative') {
        if (!categories.includes('Creative')) categories.push('Creative');
      } else if (normalized === 'content') {
        if (!categories.includes('Content')) categories.push('Content');
      } else if (normalized === 'news') {
        if (!categories.includes('News')) categories.push('News');
      }
    });
    
    return categories;
  };

  const getCategoryGradient = (categories) => {
    if (!categories || categories.length === 0) return '#e0e0e0';
    if (categories.length === 1) return getCategoryColor(categories[0]);
    
    // Create gradient with equal sections for each category
    const percentage = 100 / categories.length;
    const gradientStops = categories.map((cat, index) => {
      const start = index * percentage;
      const end = (index + 1) * percentage;
      return `${getCategoryColor(cat)} ${start}%, ${getCategoryColor(cat)} ${end}%`;
    }).join(', ');
    
    return `linear-gradient(to bottom, ${gradientStops})`;
  };

  // Modal configurations
  const modalConfigs = {
    'source-request': {
      title: 'Request a Source',
      subtitle: 'Reach out to the community and see if anyone has a contact for you...',
      placeholder: 'Enter your request subject...',
      detailsPlaceholder: 'Describe what kind of source you\'re looking for...',
      buttonText: 'Submit Request'
    },
    'steal-pitch': {
      title: 'Steal This Pitch',
      subtitle: 'Have an idea that someone else in the community should write?',
      placeholder: 'Enter your pitch idea...',
      detailsPlaceholder: 'Describe your pitch idea...',
      buttonText: 'Submit Pitch'
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!modalSubject.trim() || !modalContent.trim()) {
      toast.error('Please fill in both subject and content');
      return;
    }

    setIsSubmitting(true);
    try {
      const prefix = modalType === 'source-request' ? 'SOURCE REQUEST:' : 'STEAL THIS PITCH:';
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/forum/threads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: `${prefix} ${modalSubject.trim()}`,
          content: modalContent.trim(),
          type: modalType === 'source-request' ? 'source-request' : 'steal-pitch'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create thread');
      }

      // Reset form and close modal
      setModalSubject('');
      setModalContent('');
      setShowModal(false);
      setModalType(null);
      
      // Show success message
      toast.success('Submission successful! Your request has been posted to the forum.');
    } catch (err) {
      console.error('Error creating thread:', err);
      toast.error('Failed to create thread: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh'
      }}>
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }


  if (!isAuthenticated) {
    return (
      <div style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h1 style={{ 
          fontSize: '3rem',
          fontWeight: '700',
          color: '#333',
          marginBottom: '1rem'
        }}>
          Welcome to Study Hall
        </h1>
        <p style={{ 
          fontSize: '1.25rem',
          color: '#666',
          marginBottom: '2rem',
          lineHeight: '1.6'
        }}>
          Join our community of media professionals and discover new opportunities, 
          share your work, and connect with like-minded individuals.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => { trackEvent('hp_login', 'homepage', 'Log In'); loginWithPopup(); }}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
          >
            Log In
          </button>
          <button 
            onClick={() => { trackEvent('hp_sign_up', 'homepage', 'Sign Up'); loginWithPopup({ authorizationParams: { screen_hint: 'signup' } }); }}
            style={{
              backgroundColor: 'transparent',
              color: '#38b44a',
              border: '2px solid #38b44a',
              padding: '10px 22px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#38b44a';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#38b44a';
            }}
          >
            Sign Up
          </button>
        </div>
      </div>
    );
  }

  // Remove error display - just show loading spinner and let React Query retry


  return (
    <div className="home-container" style={{ 
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>

      {/* Main Content Layout */}
      <div className="homepage-layout">
        {/* Community Previews Grid */}
        <div className="community-preview-grid">
        {/* Latest Podcast Episode Section */}
        {latestEpisode && (
          <div style={{
            background: 'linear-gradient(135deg, #f8fffe 0%, #ffffff 100%)',
            borderRadius: '12px',
            padding: '1rem 1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e9ecef',
            borderLeft: '4px solid #38b44a',
            gridColumn: 'span 2'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h2 style={{ 
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#333',
                margin: 0
              }}>
                Latest Podcast Episode
              </h2>
              <button 
                onClick={() => { trackEvent('hp_podcast_view_all', 'homepage', 'View All Episodes'); navigate('/podcast'); }}
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
              >
                View All Episodes
              </button>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#333',
                  marginTop: 0,
                  marginBottom: 0,
                  lineHeight: '1.3'
                }}>
                  {latestEpisode.title}
                </h3>
                {latestEpisode.description && (
                  <p style={{
                    fontSize: '1rem',
                    color: '#666',
                    lineHeight: '1.6',
                    marginTop: '0.25rem',
                    marginBottom: 0
                  }}>
                    {latestEpisode.description.length > 200 
                      ? latestEpisode.description.substring(0, 200) + '...' 
                      : latestEpisode.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Featured Ads Section */}
        {ads && ads.length > 0 && ads.map((ad) => (
          <div
            key={ad.id}
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e9ecef',
              borderLeft: '4px solid #38b44a',
              gridColumn: 'span 2'
            }}
          >
            {(ad.subtitle || ad.company) && (
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#333',
                margin: 0,
                marginBottom: '1rem'
              }}>
                {ad.subtitle || ad.company}
              </h2>
            )}
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                {ad.title && (
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#333',
                    marginTop: 0,
                    marginBottom: 0,
                    lineHeight: '1.3'
                  }}>
                    {ad.title}
                  </h3>
                )}
                {ad.text && (
                  <div
                    style={{
                      fontSize: '1rem',
                      color: '#666',
                      lineHeight: '1.6',
                      marginTop: '0.25rem',
                      marginBottom: ad.call_to_action_text ? '1rem' : 0
                    }}
                    dangerouslySetInnerHTML={{ __html: ad.text }}
                  />
                )}
                {ad.call_to_action_text && (
                  ad.call_to_action_url ? (
                    <a
                      href={ad.call_to_action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('hp_course_cta', 'homepage', [ad.company, ad.title].filter(Boolean).join(' – '))}
                      style={{
                        display: 'inline-block',
                        backgroundColor: ad.call_to_action_color || '#38b44a',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => { e.target.style.backgroundColor = '#2d8f3a'; }}
                      onMouseOut={(e) => { e.target.style.backgroundColor = ad.call_to_action_color || '#38b44a'; }}
                    >
                      {ad.call_to_action_text}
                    </a>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block',
                        backgroundColor: ad.call_to_action_color || '#38b44a',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'default'
                      }}
                    >
                      {ad.call_to_action_text}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Paid Ads Section */}
        {paidAds && paidAds.length > 0 && paidAds.map((ad) => (
          <div
            key={ad.id}
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e9ecef',
              borderLeft: '4px solid #38b44a',
              gridColumn: 'span 2'
            }}
          >
            {(ad.subtitle || ad.company) && (
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#333',
                margin: 0,
                marginBottom: '1rem'
              }}>
                {ad.subtitle || ad.company}
              </h2>
            )}
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                {ad.title && (
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#333',
                    marginTop: 0,
                    marginBottom: 0,
                    lineHeight: '1.3'
                  }}>
                    {ad.title}
                  </h3>
                )}
                {ad.text && (
                  <div
                    style={{
                      fontSize: '1rem',
                      color: '#666',
                      lineHeight: '1.6',
                      marginTop: '0.25rem',
                      marginBottom: ad.call_to_action_text ? '1rem' : 0
                    }}
                    dangerouslySetInnerHTML={{ __html: ad.text }}
                  />
                )}
                {ad.call_to_action_text && (
                  ad.call_to_action_url ? (
                    <a
                      href={ad.call_to_action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('hp_ad_cta', 'homepage', [ad.company, ad.title].filter(Boolean).join(' – '))}
                      style={{
                        display: 'inline-block',
                        backgroundColor: ad.call_to_action_color || '#38b44a',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => { e.target.style.backgroundColor = '#2d8f3a'; }}
                      onMouseOut={(e) => { e.target.style.backgroundColor = ad.call_to_action_color || '#38b44a'; }}
                    >
                      {ad.call_to_action_text}
                    </a>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block',
                        backgroundColor: ad.call_to_action_color || '#38b44a',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'default'
                      }}
                    >
                      {ad.call_to_action_text}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Opportunities */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ 
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#333',
              margin: 0
            }}>
              Latest Opportunities
            </h2>
            <button 
              onClick={() => { trackEvent('hp_opportunities_view_all', 'homepage', 'View All'); navigate('/opportunities'); }}
              style={{
                backgroundColor: '#38b44a',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              View All
            </button>
          </div>
          {loadingOpportunities ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '2rem'
            }}>
              <LoadingSpinner size="small" message="Loading opportunities..." />
            </div>
          ) : opportunities && opportunities.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {opportunities.map((opp) => (
                <div 
                  key={opp.id}
                  style={{
                    display: 'flex',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onClick={() => { trackEvent('hp_opportunity_card', 'homepage', opp.title || opp.hash); navigate(`/opportunity/${opp.hash}`); }}
                >
                  {/* Colored vertical line */}
                  <div style={{
                    width: '6px',
                    background: getCategoryGradient(getOpportunityCategories(opp)),
                    flexShrink: 0
                  }}></div>
                  <div style={{ padding: '0.75rem', flex: 1 }}>
                    <h3 style={{ 
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#333',
                      margin: '0 0 0.25rem 0',
                      lineHeight: '1.3'
                    }}>
                      {opp.title}
                    </h3>
                    <p style={{ 
                      fontSize: '0.8rem',
                      color: '#666',
                      margin: '0 0 0.25rem 0',
                      lineHeight: '1.3'
                    }}>
                      {opp.brand_name} • {formatTypeLabels(opp.type)}
                    </p>
                    <p style={{ 
                      fontSize: '0.75rem',
                      color: '#999',
                      margin: 0
                    }}>
                      {formatDate(opp.created)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, color: '#888', fontSize: '0.875rem' }}>No opportunities to show right now.</p>
          )}
        </div>

        {/* Forum Posts */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ 
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#333',
              margin: 0
            }}>
              Recent Forum Posts
            </h2>
            <button 
              onClick={() => { trackEvent('hp_forum_view_all', 'homepage', 'View All'); navigate('/forum'); }}
              style={{
                backgroundColor: '#38b44a',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              View All
            </button>
          </div>
          {loadingForum ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '2rem'
            }}>
              <LoadingSpinner size="small" message="Loading forum posts..." />
            </div>
          ) : forumLoadError ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <p style={{ color: '#666', margin: '0 0 0.75rem 0', fontSize: '0.875rem' }}>
                Forum preview couldn&apos;t load. You can still open the full forum.
              </p>
              <button
                type="button"
                onClick={() => refetchForumPosts()}
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginRight: '8px'
                }}
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => { trackEvent('hp_forum_view_all', 'homepage', 'View All'); navigate('/forum'); }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#38b44a',
                  border: '1px solid #38b44a',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Open forum
              </button>
            </div>
          ) : forumPosts && forumPosts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {forumPosts.map((post) => (
                <div
                  key={post.id}
                  style={{
                    display: 'flex',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onClick={() => { trackEvent('hp_forum_post_card', 'homepage', post.subject || String(post.id)); navigate(`/forum/${post.id}`); }}
                >
                  {/* Colored vertical line */}
                  <div style={{
                    width: '6px',
                    background: getThreadTypeColor(post.type || 'discussion'),
                    flexShrink: 0
                  }}></div>
                  <div style={{ padding: '0.75rem', flex: 1 }}>
                  <h3 style={{ 
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#333',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {post.subject}
                  </h3>
                  <p style={{ 
                    fontSize: '0.8rem',
                    color: '#666',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {getAuthorName(post)} • {post.post_count} {post.post_count === 1 ? 'reply' : 'replies'}
                  </p>
                  <p style={{ 
                    fontSize: '0.75rem',
                    color: '#999',
                    margin: 0
                  }}>
                    {formatDateAgo(post.email_date || post.created_at)}
                  </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <button
                type="button"
                onClick={() => { trackEvent('hp_forum_view_all', 'homepage', 'View All'); navigate('/forum'); }}
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Browse discussions
              </button>
            </div>
          )}
        </div>

        {/* Plug Your Work */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ 
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#333',
              margin: 0
            }}>
              Featured Work
            </h2>
            <button 
              onClick={() => { trackEvent('hp_plug_your_work_view_all', 'homepage', 'View All'); navigate('/plug-your-work'); }}
              style={{
                backgroundColor: '#38b44a',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              View All
            </button>
          </div>
          {loadingPlugYourWork ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '2rem'
            }}>
              <LoadingSpinner size="small" message="Loading featured work..." />
            </div>
          ) : plugYourWork && plugYourWork.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {plugYourWork.map((work) => (
                <div 
                  key={work.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onClick={() => { trackEvent('hp_plug_your_work_card', 'homepage', work.headline || work.url); window.open(work.url, '_blank'); }}
                >
                  <h3 style={{ 
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#333',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {work.headline}
                  </h3>
                  <p style={{ 
                    fontSize: '0.8rem',
                    color: '#666',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {getAuthorName(work)} • {work.publication}
                  </p>
                  <p style={{ 
                    fontSize: '0.75rem',
                    color: '#999',
                    margin: 0
                  }}>
                    {formatDate(work.posted)}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Blog Posts */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ 
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#333',
              margin: 0
            }}>
              Latest Blog Posts
            </h2>
            <button 
              onClick={() => { trackEvent('hp_blog_view_all', 'homepage', 'View All'); navigate('/blog'); }}
              style={{
                backgroundColor: '#38b44a',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              View All
            </button>
          </div>
          {loadingBlog ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '2rem'
            }}>
              <LoadingSpinner size="small" message="Loading blog posts..." />
            </div>
          ) : blogPosts && blogPosts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {blogPosts.map((post) => (
                <div 
                  key={post.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onClick={() => { trackEvent('hp_blog_post_card', 'homepage', post.title || post.slug); navigate(`/blog/${post.slug}`); }}
                >
                  <h3 style={{ 
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#333',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {post.title}
                  </h3>
                  <p style={{ 
                    fontSize: '0.8rem',
                    color: '#666',
                    margin: '0 0 0.25rem 0',
                    lineHeight: '1.3'
                  }}>
                    {post.excerpt && post.excerpt.length > 100 
                      ? post.excerpt.substring(0, 100) + '...' 
                      : post.excerpt || 'No excerpt available'
                    }
                  </p>
                  <p style={{ 
                    fontSize: '0.75rem',
                    color: '#999',
                    margin: 0
                  }}>
                    {formatDate(post.date)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '1rem', margin: 0, fontSize: '0.875rem' }}>
              {blogError ? 'Blog posts unavailable.' : 'No blog posts to show.'}
            </p>
          )}
        </div>
        </div>

        {/* Right Sidebar */}
        <div className="sidebar" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          height: 'fit-content'
        }}>
          <h2 style={{ 
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            Quick Actions
          </h2>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <button 
              onClick={() => { trackEvent('hp_quick_browse_opportunities', 'homepage', 'Browse Opportunities'); navigate('/opportunities'); }}
              style={{
                backgroundColor: '#38b44a',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
            >
              Browse Opportunities
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_share_your_work', 'homepage', 'Share Your Work'); navigate('/plug-your-work/submit'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Share Your Work
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_join_discussion', 'homepage', 'Join Discussion'); navigate('/forum'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Join Discussion
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_search_editors', 'homepage', 'Search Editors'); navigate('/editor-database'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Search Editors
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_source_request', 'homepage', 'Source Request'); openModal('source-request'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Source Request
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_steal_pitch', 'homepage', 'Steal This Pitch'); openModal('steal-pitch'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Steal This Pitch
            </button>
            <button 
              onClick={() => { trackEvent('hp_quick_podcast', 'homepage', 'Podcast Episodes'); navigate('/podcast'); }}
              style={{
                backgroundColor: 'transparent',
                color: '#38b44a',
                border: '2px solid #38b44a',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }}
            >
              Podcast Episodes
            </button>
          </div>
        </div>
      </div>

      {/* Unified Modal */}
      {showModal && modalType && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#333',
                margin: 0
              }}>
                {modalConfigs[modalType]?.title}
              </h2>
              <button
                onClick={() => { setShowModal(false); setModalType(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <p style={{
              fontSize: '0.875rem',
              color: '#666',
              marginTop: '-0.5rem',
              marginBottom: '1.5rem'
            }}>
              {modalConfigs[modalType]?.subtitle}
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={modalSubject}
                  onChange={(e) => setModalSubject(e.target.value)}
                  placeholder={modalConfigs[modalType]?.placeholder}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Details *
                </label>
                <textarea
                  value={modalContent}
                  onChange={(e) => setModalContent(e.target.value)}
                  placeholder={modalConfigs[modalType]?.detailsPlaceholder}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    minHeight: '200px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                  required
                />
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem'
              }}>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setModalType(null); }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !modalSubject.trim() || !modalContent.trim()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isSubmitting || !modalSubject.trim() || !modalContent.trim() ? '#9ca3af' : '#38b44a',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: isSubmitting || !modalSubject.trim() || !modalContent.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isSubmitting && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                  )}
                  {isSubmitting ? 'Submitting...' : modalConfigs[modalType]?.buttonText}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
